import { CartLinesUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

let pending = false;
let cartState = null;
const queue = new Map();
const identities = new Map();
const checkoutStates = new Map();

function groupedCount(cart) {
  const bundles = new Set();
  return (cart.items || []).reduce((count, item) => {
    const properties = item.properties || {};
    if (properties._bundle_source === 'combo-vacio-personalizado' && properties._bundle_id) {
      if (bundles.has(properties._bundle_id)) return count;
      bundles.add(properties._bundle_id);
      return count + 1;
    }
    return count + item.quantity;
  }, 0);
}

function message(component, key, values = {}) {
  const messages = JSON.parse(component.querySelector('[data-bmh-cart-messages]')?.textContent || '{}');
  return String(messages[key] || '').replaceAll('__quantity__', String(values.quantity ?? '')).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => values[name] ?? '');
}

function announce(component, text, error = false) {
  let region = component.querySelector('[data-bmh-cart-live-region]');
  if (!region) {
    region = document.createElement('p');
    region.setAttribute('data-bmh-cart-live-region', '');
    region.setAttribute('aria-live', 'polite');
    component.prepend(region);
  }
  region.className = error ? 'bmh-cart-update-error' : 'visually-hidden';
  region.setAttribute('role', error ? 'alert' : 'status');
  region.textContent = text;
}

async function renderCart(cart, ids, signal) {
  let sections = cart.sections || {};
  const missing = ids.filter(id => !sections[id]);
  if (missing.length) {
    const url = new URL(document.baseURI);
    url.searchParams.set('sections', missing.join(','));
    const response = await fetch(url, {credentials: 'same-origin', cache: 'no-store', signal});
    if (!response.ok) throw new Error('section');
    sections = {...sections, ...await response.json()};
  }
  if (ids.some(id => !sections[id])) throw new Error('section');
  const paymentButtons = [...document.querySelectorAll('cart-items-component')].map(component => ({
    id: component.dataset.sectionId,
    button: component.querySelector('scala-cart-button')
  }));
  await Promise.all(ids.map(id => morphSection(id, sections[id], {mode: id === 'cart-drawer-section' ? 'hydration' : 'full'})));
  for (const {id, button} of paymentButtons) {
    if (!button) continue;
    const component = [...document.querySelectorAll('cart-items-component')].find(item => item.dataset.sectionId === id);
    const summary = component?.querySelector('.cart-drawer__summary, .cart-page__summary');
    if (summary && !summary.querySelector('scala-cart-button')) {
      const ctas = summary.querySelector('.cart__ctas');
      if (ctas) ctas.parentNode.insertBefore(button, ctas);
    }
  }
  cart.sections = sections;
}

function identity(item) {
  const properties = Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify([item.variant_id || item.id, item.selling_plan_allocation?.selling_plan?.id || null, properties]);
}

function readState() {
  try { return JSON.parse(document.querySelector('[data-bmh-cart-state]')?.textContent || 'null'); }
  catch { return null; }
}

function setPending(value) {
  for (const component of document.querySelectorAll('cart-items-component')) {
    component.toggleAttribute('data-bmh-cart-pending', value);
    for (const button of component.querySelectorAll('.cart__checkout-button, cart-discount-component button')) {
      if (value) {
        if (!checkoutStates.has(button)) checkoutStates.set(button, button.disabled);
        button.disabled = true;
      }
    }
  }
  if (!value) {
    for (const [button, disabled] of checkoutStates) {
      if (button.isConnected) button.disabled = disabled;
    }
    checkoutStates.clear();
    document.querySelectorAll('[data-bmh-cart-quantity][aria-busy]').forEach(el => el.removeAttribute('aria-busy'));
    document.querySelectorAll('[data-bmh-removing]').forEach(el => el.removeAttribute('data-bmh-removing'));
  }
}

function showQuantity(key, quantity) {
  for (const control of document.querySelectorAll('[data-bmh-cart-quantity]')) {
    if (control.dataset.lineKey !== key) continue;
    control.dataset.currentQuantity = String(quantity);
    const value = control.querySelector('[data-bmh-quantity-value]');
    if (value) value.textContent = String(quantity);
    control.setAttribute('aria-busy', 'true');
    control.closest('.cart-items__table-row')?.toggleAttribute('data-bmh-removing', quantity === 0);
  }
}

function notifyCart(cart) {
  document.dispatchEvent(new CartLinesUpdateEvent({
    action: 'update', context: 'cart', lines: [],
    promise: Promise.resolve({cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: {
      itemCount: groupedCount(cart), items: cart.items, source: 'bmh-cart', sections: cart.sections, alreadyRendered: true
    }})
  }));
}

// Invalidate only this configurator's drafts after a confirmed cart mutation.
// Storage events reset other open product tabs; the event covers this document.
function syncComboDraftsAfterCartChange(cart) {
  if (!Array.isArray(cart?.items)) return;
  const bundleIds = new Set(cart.items.map(item => item.properties?._bundle_id).filter(Boolean));
  try {
    const keys = [];
    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (key?.startsWith('bmh:vacuum-builder:v1:')) keys.push(key);
    }
    for (const key of keys) {
      if (!cart.items.length) { localStorage.removeItem(key); continue; }
      let draft;
      try { draft = JSON.parse(localStorage.getItem(key) || 'null'); } catch { continue; }
      if (draft?.cartMode === 'bundle' && draft.bundleId && !bundleIds.has(draft.bundleId)) {
        localStorage.removeItem(key);
      }
    }
  } catch { /* The current page can still reset when storage is unavailable. */ }
  document.dispatchEvent(new CustomEvent('bmh:cart-committed', { detail: { items: cart.items } }));
}

async function flush() {
  if (pending || !queue.size) return;
  pending = true;
  setPending(true);
  document.dispatchEvent(new CustomEvent('bmh:cart-changing'));
  const ids = [...new Set([...document.querySelectorAll('cart-items-component')].map(el => el.dataset.sectionId).filter(Boolean))].slice(0, 5);
  const root = window.Shopify?.routes?.root || '/';
  const context = new URL(document.baseURI);
  const before = cartState;
  let lastJob;
  try {
    while (queue.size) {
      const [queueKey, job] = queue.entries().next().value;
      queue.delete(queueKey);
      lastJob = job;
      let payload;
      if (job.bundleKeys) {
        payload = {updates: Object.fromEntries(job.bundleKeys.map(key => [key, 0]))};
      } else {
        const exact = cartState?.items?.find(item => item.key === job.key);
        const matches = exact ? [exact] : (cartState?.items || []).filter(item => identity(item) === job.identity);
        if (matches.length !== 1) throw new Error('cart-line-changed');
        if (matches[0].quantity === job.quantity) continue;
        payload = {id: matches[0].key, quantity: job.quantity};
      }
      const response = await fetch(`${root}cart/${job.bundleKeys ? 'update' : 'change'}.js`, {
        method: 'POST', credentials: 'same-origin', signal: AbortSignal.timeout(25000),
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({...payload, sections: ids, sections_url: context.pathname + context.search})
      });
      const cart = await response.json();
      if (!response.ok || cart.errors) {
        const error = new Error(`cart-http-${response.status}`);
        if (typeof cart.description === 'string') error.customerMessage = cart.description;
        else if (typeof cart.errors === 'string') error.customerMessage = cart.errors;
        throw error;
      }
      cartState = cart;
      syncComboDraftsAfterCartChange(cartState);
    }
    await renderCart(cartState, ids, AbortSignal.timeout(15000));
    notifyCart(cartState);
    const updated = [...document.querySelectorAll('cart-items-component')].find(el => el.dataset.sectionId === lastJob?.sectionId);
    if (updated) announce(updated, lastJob.statusText);
  } catch (error) {
    queue.clear();
    try {
      const response = await fetch(`${root}cart.js`, {credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(10000)});
      if (!response.ok) throw new Error('cart-refresh');
      cartState = await response.json();
      syncComboDraftsAfterCartChange(cartState);
      await renderCart(cartState, ids, AbortSignal.timeout(10000));
      notifyCart(cartState);
    } catch {
      for (const item of before?.items || []) showQuantity(item.key, item.quantity);
    }
    const component = document.querySelector('cart-items-component');
    if (component) announce(component, error.customerMessage || message(component, 'update_error'), true);
    document.dispatchEvent(new CustomEvent('bmh:cart-change-error'));
  } finally {
    pending = false;
    setPending(false);
    if (queue.size) flush();
    else identities.clear();
  }
}

document.addEventListener('click', event => {
  if (!(event.target instanceof Element)) return;
  if (pending && event.target.closest('.bmh-cart-payment, cart-discount-component')) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }
  const button = event.target.closest('[data-bmh-quantity-action], [data-bmh-remove-line], [data-bmh-remove-bundle]');
  if (!button) return;
  event.preventDefault();
  if (button.disabled || document.querySelector('cart-discount-component[aria-busy="true"]')) return;
  const component = button.closest('cart-items-component');
  if (!component || button.closest('[data-bmh-removing]')) return;
  if (!pending) {
    cartState = readState();
    identities.clear();
    for (const item of cartState?.items || []) identities.set(item.key, identity(item));
  }
  if (!cartState) return;
  const isBundle = button.hasAttribute('data-bmh-remove-bundle');
  const isRemove = isBundle || button.hasAttribute('data-bmh-remove-line');
  const control = button.closest('[data-bmh-cart-quantity]');
  const job = {sectionId: component.dataset.sectionId};
  let queueKey;
  if (isBundle) {
    try { job.bundleKeys = JSON.parse(button.dataset.bmhBundleKeys || '[]'); } catch { return; }
    if (!job.bundleKeys.length || job.bundleKeys.some(key => typeof key !== 'string')) return;
    queueKey = job.bundleKeys.join('|');
    button.closest('.cart-items__table-row')?.setAttribute('data-bmh-removing', '');
  } else {
    job.key = isRemove ? button.dataset.lineKey : control?.dataset.lineKey;
    if (!job.key || (!isRemove && control.dataset.canUpdate === 'false')) return;
    job.identity = identities.get(job.key);
    const current = Number(control?.dataset.currentQuantity) || 0;
    const minimum = Math.max(1, Number(control?.dataset.min) || 1);
    const maximum = Number(control?.dataset.max) || 0;
    const step = Math.max(1, Number(control?.dataset.step) || 1);
    job.quantity = isRemove ? 0 : button.dataset.bmhQuantityAction === 'increase'
      ? (maximum > 0 ? Math.min(maximum, current + step) : current + step)
      : current <= minimum ? 0 : Math.max(minimum, current - step);
    if (!isRemove && job.quantity === current) return;
    queueKey = job.key;
    showQuantity(job.key, job.quantity);
  }
  job.statusText = message(component, isBundle ? 'bundle_removed' : isRemove || job.quantity === 0 ? 'removed' : 'quantity_updated', {quantity: job.quantity});
  queue.set(queueKey, job);
  announce(component, message(component, 'updating'));
  flush();
}, true);

document.addEventListener('submit', event => {
  if (pending && event.target instanceof Element && event.target.closest('cart-items-component')) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}, true);
